import { useCallback, useState } from "react";
import { formatCryptocomSymbol, useCryptocomWsConnection } from "@/hooks/exchanges/direct-ws/cryptocom";
import type { CryptocomTickerMessage } from "@/hooks/exchanges/direct-ws/cryptocom/types";
import type { RealtimeTickerData } from "@/hooks/exchanges/types";

interface UseCryptocomTickerWSResult {
  ticker: RealtimeTickerData | undefined;
  isConnected: boolean;
  error: string | null;
}

/**
 * Hook for subscribing to Crypto.com ticker data via shared WebSocket.
 * Uses the ticker.{instrument_name} channel.
 * Provides volume, price change, open interest, and more.
 */
export const useCryptocomTickerWS = ({
  pairSymbol,
  enabled = true,
}: {
  pairSymbol: string | undefined;
  enabled?: boolean;
}): UseCryptocomTickerWSResult => {
  const [ticker, setTicker] = useState<RealtimeTickerData | undefined>(undefined);
  const [tickerError, setTickerError] = useState<string | null>(null);

  const setup = useCallback(
    (ws: WebSocket) => {
      if (!pairSymbol) return;

      setTicker(undefined);
      setTickerError(null);

      const symbol = formatCryptocomSymbol(pairSymbol);
      const channel = `ticker.${symbol}`;

      ws.send(JSON.stringify({ id: 1, method: "subscribe", params: { channels: [channel] } }));

      return () => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ id: 2, method: "unsubscribe", params: { channels: [channel] } }));
        }
      };
    },
    [pairSymbol],
  );

  const onMessage = useCallback(
    (msg: unknown) => {
      if (!pairSymbol) return;
      const expectedSubscription = `ticker.${formatCryptocomSymbol(pairSymbol)}`;

      try {
        const typed = msg as CryptocomTickerMessage;

        if (typed.result?.subscription === expectedSubscription && typed.result?.data?.[0]) {
          const d = typed.result.data[0];

          setTicker({
            volume24h: d.v ?? "0",
            volumeUsd24h: d.vv ?? "0",
            priceChange24h: parseFloat(d.c ?? "0") * 100,
            openInterest: d.oi ?? "0",
            lastPrice: d.a ?? "0",
            high24h: d.h ?? "0",
            low24h: d.l ?? "0",
            bestBid: d.b ?? "0",
            bestAsk: d.k ?? "0",
          });
          setTickerError(null);
        }
      } catch {
        setTickerError("Failed to parse ticker update");
      }
    },
    [pairSymbol],
  );

  const { isConnected, error: connectionError } = useCryptocomWsConnection({
    setup,
    onMessage,
    enabled: enabled && !!pairSymbol,
  });

  return {
    ticker,
    isConnected,
    error: tickerError ?? connectionError,
  };
};
